/**
 * ─── POS Products Page ───────────────────────────────────────
 * Product catalog management for the POS system.
 * Add, edit, view products with categories, barcodes, and pricing.
 */

import { useEffect, useState, useCallback } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { posProductRepo, posCategoryRepo } from '../../../lib/db'
import type { POSProduct, POSCategory } from '@repo/entity-pos'
import {
  PRODUCT_UNIT_LABELS, PRODUCT_STATUS_LABELS, PRODUCT_STATUS_COLORS,
} from '@repo/entity-pos'
import { POSProductService } from '@repo/entity-pos'
import {
  Plus, Search, Package, Barcode, Pencil, AlertTriangle,
  PackageCheck, PackageX, TrendingUp, RefreshCw,
} from 'lucide-react'

export function POSProductsPage() {
  const [products, setProducts] = useState<POSProduct[]>([])
  const [categories, setCategories] = useState<POSCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [showAdd, setShowAdd] = useState(false)

  // Form state
  const [form, setForm] = useState({
    sku: '',
    barcode: '',
    name: '',
    description: '',
    categoryId: '',
    unit: 'piece' as string,
    retailPrice: '0',
    costPrice: '0',
    stockOnHand: '0',
    minStockLevel: '5',
    maxStockLevel: '100',
    status: 'active' as string,
    isSellable: true,
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [prodResult, catResult] = await Promise.all([
        posProductRepo.findMany({ page: 1, pageSize: 500, sort: [{ field: 'sortOrder', direction: 'asc' }] }),
        posCategoryRepo.findMany({ page: 1, pageSize: 100 }),
      ])
      if ('items' in prodResult) setProducts(prodResult.items as POSProduct[])
      if ('items' in catResult) setCategories(catResult.items as POSCategory[])
    } catch (err) {
      console.error('Products load error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = products.filter((p) => {
    if (search) {
      const q = search.toLowerCase()
      if (!p.name.toLowerCase().includes(q) && !p.sku.toLowerCase().includes(q) && !(p.barcode && p.barcode.includes(q))) {
        return false
      }
    }
    if (statusFilter && p.status !== statusFilter) return false
    if (categoryFilter && p.categoryId !== categoryFilter) return false
    return true
  })

  const totalValue = filtered.reduce((sum, p) => sum + (p.stockOnHand ?? 0) * (p.retailPrice ?? 0), 0)
  const totalCost = filtered.reduce((sum, p) => sum + (p.stockOnHand ?? 0) * (p.costPrice ?? 0), 0)
  const lowStock = filtered.filter((p) => POSProductService.isLowStock(p) && p.status === 'active').length
  const outOfStock = filtered.filter((p) => POSProductService.isOutOfStock(p) && p.status === 'active').length

  const handleAdd = async () => {
    if (!form.name || !form.sku) return
    try {
      const product: POSProduct = {
        id: crypto.randomUUID(),
        tenantId: 'default',
        sku: form.sku,
        barcode: form.barcode || undefined,
        name: form.name,
        description: form.description || undefined,
        categoryId: form.categoryId || undefined,
        categoryName: categories.find((c) => c.id === form.categoryId)?.name,
        unit: form.unit as any,
        retailPrice: parseFloat(form.retailPrice) || 0,
        costPrice: parseFloat(form.costPrice) || 0,
        markupPercent: POSProductService.computeMarkupPercent(parseFloat(form.costPrice) || 0, parseFloat(form.retailPrice) || 0),
        taxType: 'vat',
        vatRate: 0.12,
        isVatInclusive: true,
        stockOnHand: parseInt(form.stockOnHand) || 0,
        minStockLevel: parseInt(form.minStockLevel) || 5,
        maxStockLevel: parseInt(form.maxStockLevel) || 100,
        isSellable: form.isSellable,
        allowDiscount: true,
        sortOrder: products.length + 1,
        status: form.status as any,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        deletedAt: null,
        version: 1,
        createdBy: 'admin',
        updatedBy: 'admin',
      }
      await posProductRepo.create(product)
      setShowAdd(false)
      setForm({ sku: '', barcode: '', name: '', description: '', categoryId: '', unit: 'piece', retailPrice: '0', costPrice: '0', stockOnHand: '0', minStockLevel: '5', maxStockLevel: '100', status: 'active', isSellable: true })
      load()
    } catch (err) {
      console.error('Add product error:', err)
    }
  }

  const getStockColor = (p: POSProduct): 'green' | 'yellow' | 'red' | 'gray' => {
    const status = POSProductService.getStockStatus(p)
    if (status === 'in_stock') return 'green'
    if (status === 'low_stock') return 'yellow'
    if (status === 'out_of_stock') return 'red'
    return 'gray'
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-500">POS product catalog</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} icon={<RefreshCw className="h-4 w-4" />}>Refresh</Button>
          <Button onClick={() => setShowAdd(true)} icon={<Plus className="h-4 w-4" />}>Add Product</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KpiCard icon={<Package className="h-5 w-5 text-blue-600" />} label="Total Products" value={filtered.length} color="blue" />
        <KpiCard icon={<TrendingUp className="h-5 w-5 text-green-600" />} label="Inventory Value" value={`₱${totalValue.toLocaleString()}`} color="green" />
        <KpiCard icon={<AlertTriangle className="h-5 w-5 text-yellow-600" />} label="Low Stock" value={lowStock} color="yellow" />
        <KpiCard icon={<PackageX className="h-5 w-5 text-red-600" />} label="Out of Stock" value={outOfStock} color="red" />
      </div>

      {/* Products Table */}
      <Card>
        <CardHeader title="Product Catalog" description={`${filtered.length} products`} />
        <div className="mb-4 flex gap-3 px-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by name, SKU, or barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="">All Statuses</option>
            {Object.entries(PRODUCT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-4 py-3 text-left font-medium text-gray-600">SKU</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Product</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Category</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Price</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Cost</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Margin</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Stock</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No products found</td></tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-500">{p.sku}</span>
                      {p.barcode && <Barcode className="ml-1 inline-block h-3 w-3 text-gray-400" />}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{p.name}</p>
                      {p.description && <p className="text-xs text-gray-500">{p.description}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{p.categoryName || '-'}</td>
                    <td className="px-4 py-3 text-right font-medium">₱{(p.retailPrice ?? 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">₱{(p.costPrice ?? 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={(p.markupPercent ?? 0) >= 30 ? 'text-green-600' : 'text-yellow-600'}>
                        {(p.markupPercent ?? 0).toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Badge color={getStockColor(p)} size="sm">
                          {p.stockOnHand} {PRODUCT_UNIT_LABELS[p.unit]?.toLowerCase() || ''}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge color={PRODUCT_STATUS_COLORS[p.status]} size="sm">
                        {PRODUCT_STATUS_LABELS[p.status]}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Product Modal */}
      {showAdd && (
        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Product">
          <div className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">SKU *</label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="e.g. PROD-001" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Barcode</label>
                <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="UPC/EAN code" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Category</label>
                <select
                  value={form.categoryId}
                  onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                >
                  <option value="">None</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Unit</label>
                <select
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                >
                  {Object.entries(PRODUCT_UNIT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Retail Price ₱</label>
                <Input type="number" value={form.retailPrice} onChange={(e) => setForm({ ...form, retailPrice: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Cost Price ₱</label>
                <Input type="number" value={form.costPrice} onChange={(e) => setForm({ ...form, costPrice: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                >
                  {Object.entries(PRODUCT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Stock Qty</label>
                <Input type="number" value={form.stockOnHand} onChange={(e) => setForm({ ...form, stockOnHand: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Min Stock</label>
                <Input type="number" value={form.minStockLevel} onChange={(e) => setForm({ ...form, minStockLevel: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Max Stock</label>
                <Input type="number" value={form.maxStockLevel} onChange={(e) => setForm({ ...form, maxStockLevel: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isSellable}
                onChange={(e) => setForm({ ...form, isSellable: e.target.checked })}
                className="rounded"
              />
              <span className="text-xs text-gray-700">Sellable at POS register</span>
            </label>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowAdd(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAdd} className="flex-1">Save Product</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function KpiCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string | number; color: string }) {
  const bgMap: Record<string, string> = { blue: 'bg-blue-50 border-blue-200', green: 'bg-green-50 border-green-200', yellow: 'bg-yellow-50 border-yellow-200', red: 'bg-red-50 border-red-200' }
  return (
    <div className={`rounded-xl border p-4 ${bgMap[color] || 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-1">{icon}<p className="text-xs text-gray-600">{label}</p></div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  )
}
